import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Collapse } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~/app/auth/context/AuthContext';
import { MaintenanceExecutionService } from '~/app/services/maintenance-execution/maintenance-execution.service';
import { UserService } from '~/app/services/user/user.service';
import { TicketDetailTimeline } from '~/app/components/maintenance/work-orders/TicketDetailPageContainer/components/TicketDetailTimeline/TicketDetailTimeline';
import { WORK_ORDER_UI } from '~/app/components/maintenance/work-orders/work-order-ui-colors';

export interface TicketTimelineCollapseProps {
  ticketId: string;
}

export const TicketTimelineCollapse: FC<TicketTimelineCollapseProps> = ({
  ticketId,
}) => {
  const { t } = useTranslation('maintenance');
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? '';
  const [open, setOpen] = useState(false);

  const ticketQuery = useQuery({
    queryKey: ['maintenance-execution', 'ticket', ticketId, organizationId],
    queryFn: () =>
      MaintenanceExecutionService.getTicket(ticketId, organizationId),
    enabled: Boolean(open && ticketId && organizationId),
    refetchOnMount: 'always',
  });

  const usersQuery = useQuery({
    queryKey: ['users', 'org-lookup', organizationId],
    queryFn: () => UserService.listOrganizationUsers(organizationId),
    enabled: Boolean(open && organizationId),
    staleTime: 60_000,
  });

  const events = useMemo(
    () =>
      [...(ticketQuery.data?.events ?? [])].sort(
        (a, b) => b.occurredAt.getTime() - a.occurredAt.getTime(),
      ),
    [ticketQuery.data?.events],
  );

  const actorNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const orgUser of usersQuery.data ?? []) {
      map.set(orgUser.id, orgUser.name);
    }
    return map;
  }, [usersQuery.data]);

  return (
    <Collapse
      ghost
      size="small"
      style={{ marginTop: 4 }}
      activeKey={open ? ['timeline'] : []}
      onChange={(keys) => {
        const next = Array.isArray(keys) ? keys : [keys];
        setOpen(next.includes('timeline'));
      }}
      items={[
        {
          key: 'timeline',
          label: (
            <span style={{ color: WORK_ORDER_UI.sidebarNavy, fontWeight: 500 }}>
              <HistoryOutlined style={{ marginRight: 6 }} />
              {t('workOrders.kanban.detail.ticketTimeline')}
            </span>
          ),
          children: (
            <TicketDetailTimeline
              events={events}
              actorNameById={actorNameById}
              loading={ticketQuery.isLoading || ticketQuery.isFetching}
              compact
              showHint={false}
            />
          ),
        },
      ]}
    />
  );
};
